import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import { apiClient } from '../../apiClient';
import { auth } from '../../firebase';
import { writeAuditLog } from '../../lib/auditLogger';

// 日時フォーマット関数
const formatDate = (timestamp) => {
  if (!timestamp) return 'N/A';
  const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp);
  if (isNaN(date.getTime())) return 'Invalid Date';
  return date.toLocaleString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
};

// Stripe連携ログ管理ページ
const StripeLogs = () => {
  const location = useLocation();
  const [logs, setLogs] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);
  const [lastDocTimestamp, setLastDocTimestamp] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const [searchInputs, setSearchInputs] = useState({
    billwaveCustomerId: '',
    processed_by: '',
    customer_id: '',
    invoice_id: '',
    result: '',
    error_code: '',
    startDate: '',
    endDate: ''
  });

  const [activeFilters, setActiveFilters] = useState({});

  const fetchLogs = useCallback(async (filters, isLoadMore = false, currentLastDocTimestamp = null) => {
    if (isLoadMore ? loadingMore : loading) return;
    isLoadMore ? setLoadingMore(true) : setLoading(true);

    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');
      const token = await user.getIdToken();
      const config = { headers: { 'Authorization': `Bearer ${token}` } };

      const params = { ...filters };
      const effectiveLastDocTimestamp = isLoadMore ? (currentLastDocTimestamp || lastDocTimestamp) : null;
      if (effectiveLastDocTimestamp) {
        params.lastDocTimestamp = new Date(effectiveLastDocTimestamp).getTime();
      }

      const queryString = new URLSearchParams(params).toString();
      const response = await apiClient.get(`/v1/admin/stripe-logs?${queryString}`, config);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch Stripe logs');
      }
      
      setLogs(prev => isLoadMore ? [...prev, ...data] : data);
      
      if (data.length < 50) {
        setHasMore(false);
      } else {
        setLastDocTimestamp(data[data.length - 1].processed_at);
        setHasMore(true);
      }
    } catch (error) {
      console.error('Stripe連携ログの取得中にエラーが発生しました:', error);
      setHasMore(false);
    } finally {
      isLoadMore ? setLoadingMore(false) : setLoading(false);
    }
  }, [lastDocTimestamp, loading, loadingMore]);

  // Handle initial search with query parameters from URL
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      if (user) {
        const queryParams = new URLSearchParams(location.search);
        const customerIdFromUrl = queryParams.get('billwaveCustomerId') || '';
        
        const initialFilters = {
          billwaveCustomerId: customerIdFromUrl,
          processed_by: '',
          customer_id: '',
          invoice_id: '',
          result: '',
          error_code: '',
          startDate: '',
          endDate: ''
        };

        setSearchInputs(initialFilters);
        
        const cleanFilters = Object.fromEntries(
          Object.entries(initialFilters).filter(([_, value]) => value !== '')
        );
        
        setActiveFilters(cleanFilters);
        setLogs([]);
        setLastDocTimestamp(null);
        setHasMore(true);
        fetchLogs(cleanFilters, false, null);
      }
    });
    return () => unsubscribe();
  }, [location.search]);

  const handleSearchChange = (e) => {
    setSearchInputs({ ...searchInputs, [e.target.name]: e.target.value });
  };

  const handleSearch = () => {
      const cleanFilters = Object.fromEntries(
        Object.entries(searchInputs).filter(([_, value]) => value !== '' && value !== null)
      );
      if (cleanFilters.startDate) cleanFilters.startDate = new Date(cleanFilters.startDate).toISOString();
      if (cleanFilters.endDate) cleanFilters.endDate = new Date(cleanFilters.endDate).toISOString();
      
      setActiveFilters(cleanFilters);
      setLastDocTimestamp(null);
      setLogs([]);
      fetchLogs(cleanFilters, false, null);
  };

  const handleReset = () => {
      const emptyInputs = { billwaveCustomerId: '', processed_by: '', customer_id: '', invoice_id: '', result: '', error_code: '', startDate: '', endDate: '' };
      setSearchInputs(emptyInputs);
      setActiveFilters({});
      setLastDocTimestamp(null);
      setLogs([]);
      fetchLogs({}, false, null);
  };
  
  const handleLoadMore = () => {
    if (hasMore && !loadingMore) {
        fetchLogs(activeFilters, true);
    }
  }

  const toggleLogDetails = (log) => {
    const isOpening = !selectedLog || selectedLog.id !== log.id;
    setSelectedLog(prev => (prev && prev.id === log.id) ? null : log);

    if (isOpening) {
      writeAuditLog({
        action: 'STRIPELOG_VIEW',
        resourceType: 'stripe_log',
        resourceId: log.id || '',
        metadata: { source: 'admin_console' }
      });
    }
  };
  
  const LogRow = ({ log }) => (
    <>
      <tr onClick={() => toggleLogDetails(log)} className="cursor-pointer hover:bg-gray-50">
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{formatDate(log.processed_at)}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{log.billwaveCustomerId}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{log.customer_id}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm">
          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${log.result === 'OK' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {log.result}
          </span>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
          <button className="text-indigo-600 hover:text-indigo-900">
            {selectedLog && selectedLog.id === log.id ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
          </button>
        </td>
      </tr>
      {selectedLog && selectedLog.id === log.id && (
        <tr>
          <td colSpan="5" className="px-2 py-2 md:px-4 md:py-4 bg-gray-50">
            <LogDetails log={selectedLog} />
          </td>
        </tr>
      )}
    </>
  );

  const LogDetails = ({ log }) => (
    <div className="bg-white p-4 rounded-lg shadow-inner text-sm">
       <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div><strong>請求日時:</strong><p className="text-gray-700">{formatDate(log.processed_at)}</p></div>
        <div><strong>Billwaveお客様ID:</strong><p className="text-gray-700">{log.billwaveCustomerId}</p></div>
        <div><strong>実行ユーザー:</strong><p className="text-gray-700">{log.processed_by}</p></div>
        <div><strong>Stripe顧客ID:</strong><p className="text-gray-700">{log.customer_id}</p></div>
        <div><strong>StripeインボイスID:</strong><p className="text-gray-700">{log.invoice_id || 'N/A'}</p></div>
        <div><strong>結果:</strong> <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${log.result === 'OK' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{log.result}</span></div>
        <div className="col-span-full">
          <strong>請求書PDF:</strong>
          {log.invoice_pdf ? (
            <a href={log.invoice_pdf} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800 flex items-center">
              <p className="truncate mr-2">{log.invoice_pdf}</p>
              <ExternalLink size={16} />
            </a>
          ) : <p className="text-gray-700">N/A</p>}
        </div>
        {log.result !== 'OK' && (
          <>
            {log.error_code && 
              <div><strong>エラーコード:</strong><p className="text-red-700">{log.error_code}</p></div>
            }
            {log.error_reason &&
              <div className="col-span-full">
                  <strong>Stripeエラーメッセージ:</strong>
                  <p className="text-red-700 bg-red-50 p-2 rounded mt-1">{log.error_reason}</p>
              </div>
            }
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <h2 className="text-2xl font-bold mb-6">Stripe連携ログ</h2>
      {/* 検索フォーム */}
      <div className="p-4 bg-gray-50 rounded-lg mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <input type="text" name="billwaveCustomerId" placeholder="Billwave顧客ID" value={searchInputs.billwaveCustomerId} onChange={handleSearchChange} className="p-2 border rounded shadow-sm"/>
          <input type="text" name="processed_by" placeholder="実行ユーザーID" value={searchInputs.processed_by} onChange={handleSearchChange} className="p-2 border rounded shadow-sm"/>
          <input type="text" name="customer_id" placeholder="Stripe顧客ID" value={searchInputs.customer_id} onChange={handleSearchChange} className="p-2 border rounded shadow-sm"/>
          <input type="text" name="invoice_id" placeholder="StripeインボイスID" value={searchInputs.invoice_id} onChange={handleSearchChange} className="p-2 border rounded shadow-sm"/>
          <select name="result" value={searchInputs.result} onChange={handleSearchChange} className="p-2 border rounded shadow-sm bg-white">
            <option value="">全ての結果</option>
            <option value="OK">OK</option>
            <option value="NG_NO_RETRY">NG_NO_RETRY</option>
            <option value="NG_RETRY">NG_RETRY</option>
          </select>
          <input type="text" name="error_code" placeholder="エラーコード" value={searchInputs.error_code} onChange={handleSearchChange} className="p-2 border rounded shadow-sm"/>
          <div className="col-span-1 md:col-span-2 lg:col-span-4 grid grid-cols-2 gap-4">
            <input type="datetime-local" name="startDate" placeholder="開始日時" value={searchInputs.startDate} onChange={handleSearchChange} className="p-2 border rounded shadow-sm"/>
            <input type="datetime-local" name="endDate" placeholder="終了日時" value={searchInputs.endDate} onChange={handleSearchChange} className="p-2 border rounded shadow-sm"/>
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
            <button onClick={handleSearch} className="px-4 py-2 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 shadow-sm disabled:bg-blue-400" disabled={loading}>検索</button>
            <button onClick={handleReset} className="px-4 py-2 bg-gray-300 text-gray-800 font-semibold rounded-md hover:bg-gray-400 shadow-sm">リセット</button>
        </div>
      </div>
      
      {/* 実行結果 */}
      {(loading && logs.length === 0) ? <p className="text-center py-4">読み込み中...</p> : logs.length === 0 ? <p className="text-center py-4 text-gray-500">ログが見つかりません。</p> : (
        <>
          <div className="overflow-x-auto shadow-md sm:rounded-lg border">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">請求日時</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Billwave顧客ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stripe顧客ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">結果</th>
                  <th className="relative px-6 py-3"><span className="sr-only">詳細</span></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {logs.map(log => <LogRow key={log.id} log={log} />)}
              </tbody>
            </table>
          </div>
          {hasMore && (
            <div className="mt-6 text-center">
              <button onClick={handleLoadMore} disabled={loadingMore} className="px-6 py-2 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400">
                {loadingMore ? '読み込み中...' : '次の50件を読み込む'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default StripeLogs;
